import { useCallback, useEffect, useState } from "react";
import { ProviderRow } from "@/app/types/Provider";
import { adminRoutes } from "../lib/api-client/adminRoutes";
import { publicRoutes } from "../lib/api-client/publicRoutes";
import { apiFetch } from "../lib/api-client/base";
import { useAdminData } from "../admin/context/AdminDataContext";

/**
 * useProviders
 * - mode: "public"  → wait until clinicId is provided, then fetch
 * - mode: "admin"   → reads from AdminDataContext cache (fetched once in layout)
 */
export function useProviders(opts: {
  clinicId?: string;
  nextAvailableSlot?: boolean;
  mode: "public" | "admin";
}) {
  const ctx = useAdminData();

  // ── Local state (used only in public mode or outside the admin layout) ──
  const [localProviders, setLocalProviders] = useState<ProviderRow[]>([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const fetchLocalProviders = useCallback(async () => {
    if (opts?.mode === "public" && !opts.clinicId) {
      setLocalProviders([]);
      return;
    }
    setLocalLoading(true);
    setLocalError(null);
    try {
      const route =
        opts.mode === "admin"
          ? adminRoutes.providers.get()
          : publicRoutes.providers.get(opts.clinicId!, opts.nextAvailableSlot);

      const response = await apiFetch(route);
      setLocalProviders(response?.items ?? []);
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : "An error occurred");
    } finally {
      setLocalLoading(false);
    }
  }, [opts?.clinicId, opts?.mode, opts?.nextAvailableSlot]);

  useEffect(() => {
    // Skip local fetch when the context is handling it (admin mode)
    if (ctx && opts.mode === "admin") return;
    fetchLocalProviders();
  }, [ctx, opts.mode, fetchLocalProviders]);

  // ── Admin mode: delegate to context ──────────────────────────────────
  if (ctx && opts.mode === "admin") {
    return {
      providers: ctx.providers,
      loading: ctx.loadingProviders,
      error: ctx.providersError,
      refreshProviders: ctx.refreshProviders,
    };
  }

  // ── Public mode (or fallback outside admin layout) ────────────────────
  return {
    providers: localProviders,
    loading: localLoading,
    error: localError,
    refreshProviders: fetchLocalProviders,
  };
}
